import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Save, X } from "lucide-react";
import { toast } from "sonner";
import CategoriesPage from "@/pages/CategoriesPage";
import ReportsPage from "@/pages/ReportsPage";
import DataTab from "@/pages/settings/DataTab";
import ImportTab from "@/pages/settings/ImportTab";
import ReconcileTab from "@/pages/settings/ReconcileTab";
import ServiceTypesTab from "@/pages/settings/ServiceTypesTab";

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const DATE_FORMATS = ["MM/DD/YYYY", "DD/MM/YYYY", "YYYY-MM-DD"];
const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD", "MXN"];

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">{label}</div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

function GeneralTab() {
  const { activeOrgId, orgs, refreshOrgs, user } = useAuth();
  const active = orgs.find((o) => o.id === activeOrgId);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (active) {
      setForm({
        name: active.name || "",
        email: active.email || "",
        phone: active.phone || "",
        website: active.website || "",
        address_line1: active.address_line1 || "",
        address_line2: active.address_line2 || "",
        city: active.city || "",
        state: active.state || "",
        postal_code: active.postal_code || "",
        country: active.country || "",
        tax_id: active.tax_id || "",
        fiscal_year_start_month: active.fiscal_year_start_month || 1,
        currency: active.currency || "USD",
        date_format: active.date_format || "MM/DD/YYYY",
      });
    }
  }, [active]);

  if (!active || !form) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/organizations/${activeOrgId}`, {
        ...form,
        fiscal_year_start_month: Number(form.fiscal_year_start_month),
      });
      await refreshOrgs();
      setEditing(false);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const cancel = () => {
    if (active) {
      setForm({
        name: active.name || "",
        email: active.email || "",
        phone: active.phone || "",
        website: active.website || "",
        address_line1: active.address_line1 || "",
        address_line2: active.address_line2 || "",
        city: active.city || "",
        state: active.state || "",
        postal_code: active.postal_code || "",
        country: active.country || "",
        tax_id: active.tax_id || "",
        fiscal_year_start_month: active.fiscal_year_start_month || 1,
        currency: active.currency || "USD",
        date_format: active.date_format || "MM/DD/YYYY",
      });
    }
    setEditing(false);
  };

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-sm bg-card rounded-3xl overflow-hidden">
        <div className="px-7 py-5 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="font-serif text-2xl">General Information</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Church details and preferences</p>
          </div>
          {!editing ? (
            <Button variant="outline" className="rounded-xl" onClick={() => setEditing(true)} data-testid="settings-edit-button">
              <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="ghost" className="rounded-xl" onClick={cancel} data-testid="settings-cancel-button">
                <X className="w-3.5 h-3.5 mr-1.5" /> Cancel
              </Button>
              <Button className="rounded-xl soft-glow" onClick={save} disabled={saving} data-testid="settings-save-button">
                <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          )}
        </div>

        <div className="px-7 py-6">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-5">Church Information</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {editing ? (
              <>
                <div className="space-y-1.5"><Label>Church Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl" data-testid="settings-org-name" /></div>
                <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl" data-testid="settings-email" /></div>
                <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl" data-testid="settings-phone" /></div>
                <div className="space-y-1.5"><Label>Website</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="rounded-xl" placeholder="https://" data-testid="settings-website" /></div>
                <div className="space-y-1.5 md:col-span-2"><Label>Address</Label><Input value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} className="rounded-xl" placeholder="Street address" data-testid="settings-address-line1" /></div>
                <div className="space-y-1.5"><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-xl" data-testid="settings-city" /></div>
                <div className="space-y-1.5"><Label>State / Province</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="rounded-xl" data-testid="settings-state" /></div>
                <div className="space-y-1.5"><Label>ZIP / Postal code</Label><Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="rounded-xl" data-testid="settings-postal-code" /></div>
                <div className="space-y-1.5"><Label>Tax ID / EIN</Label><Input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} className="rounded-xl" data-testid="settings-tax-id" /></div>
              </>
            ) : (
              <>
                <Field label="Church Name">{form.name || <span className="text-muted-foreground">Not set</span>}</Field>
                <Field label="Email">{form.email || <span className="text-muted-foreground">Not set</span>}</Field>
                <Field label="Phone">{form.phone || <span className="text-muted-foreground">Not set</span>}</Field>
                <Field label="Website">{form.website || <span className="text-muted-foreground">Not set</span>}</Field>
                <Field label="Address">{form.address_line1 || <span className="text-muted-foreground">Not set</span>}</Field>
                <Field label="City">{form.city || <span className="text-muted-foreground">Not set</span>}</Field>
                <Field label="State">{form.state || <span className="text-muted-foreground">Not set</span>}</Field>
                <Field label="ZIP Code">{form.postal_code || <span className="text-muted-foreground">Not set</span>}</Field>
                <Field label="Tax ID / EIN">{form.tax_id || <span className="text-muted-foreground">Not set</span>}</Field>
              </>
            )}
          </div>
        </div>

        <div className="px-7 py-6 border-t border-border">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-5">Financial Preferences</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {editing ? (
              <>
                <div className="space-y-1.5">
                  <Label>Fiscal Year Start</Label>
                  <Select value={String(form.fiscal_year_start_month)} onValueChange={(v) => setForm({ ...form, fiscal_year_start_month: Number(v) })}>
                    <SelectTrigger className="rounded-xl" data-testid="settings-fiscal-month"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((m, idx) => <SelectItem key={m} value={String(idx + 1)}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Currency</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger className="rounded-xl" data-testid="settings-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Date Format</Label>
                  <Select value={form.date_format} onValueChange={(v) => setForm({ ...form, date_format: v })}>
                    <SelectTrigger className="rounded-xl" data-testid="settings-date-format"><SelectValue /></SelectTrigger>
                    <SelectContent>{DATE_FORMATS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <>
                <Field label="Fiscal Year Start">{MONTHS[(form.fiscal_year_start_month || 1) - 1]}</Field>
                <Field label="Currency">$ ({form.currency})</Field>
                <Field label="Date Format">{form.date_format}</Field>
              </>
            )}
          </div>
        </div>
      </Card>

      <Card className="border-0 shadow-sm bg-card rounded-3xl px-7 py-6">
        <h3 className="font-serif text-2xl">Account</h3>
        <div className="mt-4 grid grid-cols-2 gap-6 text-sm">
          <Field label="Name">{user?.name}</Field>
          <Field label="Email">{user?.email}</Field>
        </div>
      </Card>
    </div>
  );
}

const TABS = [
  { value: "general", label: "General" },
  { value: "categories", label: "Categories" },
  { value: "service-types", label: "Service Types" },
  { value: "reports", label: "Reports" },
  { value: "data", label: "Data" },
  { value: "import", label: "Import" },
  { value: "reconcile", label: "Reconcile" },
];

export default function SettingsPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "general";

  return (
    <div className="space-y-6 fade-up">
      <header>
        <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Settings</div>
        <h1 className="font-serif text-5xl tracking-tight mt-2">Manage your church</h1>
        <p className="text-muted-foreground mt-2">Configuration, categories, funds, and reports — all in one place.</p>
      </header>

      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="bg-card rounded-2xl p-1.5 h-auto shadow-sm border border-border" data-testid="settings-tabs">
          {TABS.map((t) => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              data-testid={`settings-tab-${t.value}`}
              className="rounded-xl data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:soft-glow px-5 py-2 text-sm font-medium"
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="general" className="mt-6">
          <GeneralTab />
        </TabsContent>
        <TabsContent value="categories" className="mt-6">
          <CategoriesPage embedded />
        </TabsContent>
        <TabsContent value="service-types" className="mt-6">
          <ServiceTypesTab />
        </TabsContent>
        <TabsContent value="reports" className="mt-6">
          <ReportsPage embedded />
        </TabsContent>
        <TabsContent value="data" className="mt-6">
          <DataTab />
        </TabsContent>
        <TabsContent value="import" className="mt-6">
          <ImportTab />
        </TabsContent>
        <TabsContent value="reconcile" className="mt-6">
          <ReconcileTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
